import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, Check, Copy, X } from "lucide-react";
import Button from "../components/Button";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  getVisiblePages,
} from "../components/ui/pagination";
import styles from "./ReferralsPage.module.css";
import { useLanguage } from "../LanguageContext";
import useReferrals, { referralLink } from "../useReferrals";

const LANG_LOCALE = { EN: "en-US", ES: "es-ES", PT: "pt-BR", FR: "fr-FR", AR: "ar-EG" };

function formatDate(ts, langCode) {
  return new Intl.DateTimeFormat(LANG_LOCALE[langCode] ?? "en-US", {
    day: "2-digit", month: "long", year: "numeric",
  }).format(ts);
}

const PAGE_SIZE = 5;
// USDC on Ethereum: the API rejects anything else, so Continue stays disabled until it matches
const ERC20_ADDRESS = /^0x[0-9a-fA-F]{40}$/;

const STATUS_CLASS = { trial: "statusTrial", activated: "statusActivated", claimed: "statusClaimed" };

function SummaryCard({ label, value, detail }) {
  return (
    <article className={styles.card}>
      <p className={styles.cardLabel}>{label}</p>
      <div className={`${styles.cardValueRow} d-flex align-items-end`}>
        <strong className={styles.cardValue}>{value}</strong>
        {detail ? <span className={styles.cardDetail}>{detail}</span> : null}
      </div>
    </article>
  );
}

// Every trader can share their link, free or funded. `status` is the referrals load
// state, shown while the link isn't available yet.
function ShareLinkModal({ link, status, onClose }) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  function handleCopy() {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return createPortal(
    <div className={styles.modalOverlay} onMouseDown={onClose}>
      <div
        className={styles.modal}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
      >
        <button
          type="button"
          className={styles.modalClose}
          onClick={onClose}
          aria-label="Close"
        >
          <X size={24} />
        </button>
        <p id="share-modal-title" className={styles.modalTitle}>
          {t("ref_modal_share_title")}
        </p>
        {link ? (
          <div className={styles.modalLinkRow}>
            <input
              className={styles.modalLinkInput}
              type="text"
              readOnly
              value={link}
            />
            <Button
              type="button"
              className={styles.modalGetFunded}
              onClick={handleCopy}
            >
              {copied ? t("ref_copied_btn") : t("ref_copy_btn")} <Copy size={24} />
            </Button>
          </div>
        ) : (
          <div className={styles.modalBody}>
            <p className={styles.modalText}>
              {t(status === "error" ? "ref_load_error" : "ref_loading")}
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// Accepts a bare code or a full referral link (…/signup?ref=ABC123, or older ?referral-code=)
function parseReferralCode(input) {
  const value = input.trim();
  const match = value.match(/[?&](?:ref|referralCode|referral-code)=([^&#\s]+)/i);
  return (match ? decodeURIComponent(match[1]) : value).toUpperCase();
}

// Once a code is applied it can't be changed, so the modal just confirms which one is in use
function EnterCodeModal({ appliedCode, onApply, onClose }) {
  const { t } = useLanguage();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    const parsed = parseReferralCode(code);
    if (!parsed || submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await onApply(parsed);
    setSubmitting(false);
    // On success the modal re-renders in its "you're using the code" state
    if (result.error) setError(result.error);
  }

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className={styles.modalOverlay} onMouseDown={onClose}>
      <div
        className={styles.modal}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="enter-code-title"
      >
        <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Close">
          <X size={24} />
        </button>
        <p id="enter-code-title" className={styles.modalTitle}>{t("ref_modal_code_title")}</p>
        {appliedCode ? (
          <>
            <p className={styles.usedCodeText}>{t("ref_using_code").replace("{code}", appliedCode)}</p>
            <Button type="button" className={`${styles.modalGetFunded} ${styles.usedCodeBtn}`} disabled>
              {t("ref_enter_code")}
            </Button>
          </>
        ) : (
          <>
            <form className={styles.modalLinkRow} onSubmit={handleSubmit}>
              <input
                className={styles.modalLinkInput}
                type="text"
                placeholder={t("ref_code_placeholder")}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  setError(null);
                }}
                aria-invalid={Boolean(error)}
                autoFocus
              />
              <Button type="submit" className={styles.modalGetFunded} disabled={!parseReferralCode(code) || submitting}>
                {t("ref_enter_code")}
              </Button>
            </form>
            {error && <p className={styles.modalError} role="alert">{error}</p>}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

const formatUsd = (n) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;

// Confirming posts the request; the API answers with the reference shown on step 3
function WithdrawModal({ withdrawable, minWithdrawal, onConfirm, onClose }) {
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [walletAddress, setWalletAddress] = useState("");
  const [amount, setAmount] = useState("");
  const amountValue = Number(amount);
  const amountValid = amount !== "" && amountValue >= minWithdrawal && amountValue <= withdrawable;
  const walletValid = ERC20_ADDRESS.test(walletAddress.trim());
  const [reference, setReference] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function handleConfirm() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await onConfirm({ amount: amountValue, address: walletAddress.trim() });
    setSubmitting(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setReference(result.withdrawal.reference);
    setStep(3);
  }

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className={styles.modalOverlay} onMouseDown={onClose}>
      <div
        className={`${styles.modal} ${styles.withdrawModal}`}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          className={styles.modalClose}
          onClick={onClose}
          aria-label="Close"
        >
          <X size={24} />
        </button>

        {/* Stepper — any step that isn't current uses the muted box */}
        {step < 3 && (
          <div className={styles.withdrawStepper}>
            <div className={styles.withdrawStepItem}>
              <div className={`${styles.withdrawStepBox} ${step === 1 ? styles.withdrawStepBoxActive : ""}`}>1</div>
              <span className={`${styles.withdrawStepLabel} ${step === 1 ? styles.withdrawStepLabelActive : ""}`}>
                {t("ref_stepper_request")}
              </span>
            </div>
            <div className={styles.withdrawStepLine} />
            <div className={styles.withdrawStepItem}>
              <div className={`${styles.withdrawStepBox} ${step === 2 ? styles.withdrawStepBoxActive : ""}`}>2</div>
              <span className={`${styles.withdrawStepLabel} ${step === 2 ? styles.withdrawStepLabelActive : ""}`}>
                {t("ref_stepper_review")}
              </span>
            </div>
          </div>
        )}

        {step === 1 ? (
          <>
            <div className={styles.withdrawContent}>
              <h2 className={styles.withdrawTitle}>{t("ref_withdraw_title")}</h2>

              <div className={styles.withdrawableBox}>
                <p className={styles.withdrawableLabel}>{t("ref_withdrawable_label")}</p>
                <div className={styles.withdrawableAmount}>
                  <span className={styles.withdrawableValue}>{formatUsd(withdrawable)}</span>
                  <span className={styles.withdrawableCurrency}>USDC</span>
                </div>
              </div>

              <div className={styles.withdrawField}>
                <label className={styles.withdrawFieldLabel} htmlFor="withdraw-amount">
                  {t("ref_amount_label")}
                </label>
                <div className={styles.withdrawAmountWrap}>
                  <input
                    id="withdraw-amount"
                    className={styles.withdrawInput}
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                  />
                  <button
                    type="button"
                    className={styles.withdrawMax}
                    onClick={() => setAmount(String(withdrawable))}
                  >
                    {t("ref_max_prefix")} {withdrawable}
                  </button>
                </div>
                <p className={styles.withdrawMin}>{t("ref_min_text")}</p>
              </div>

              <div className={styles.withdrawField}>
                <label className={styles.withdrawFieldLabel} htmlFor="withdraw-wallet">
                  {t("ref_wallet_label")}
                </label>
                <input
                  id="withdraw-wallet"
                  className={styles.withdrawInput}
                  type="text"
                  value={walletAddress}
                  onChange={(e) => setWalletAddress(e.target.value)}
                />
              </div>
            </div>

            <button
              type="button"
              className={styles.withdrawContinueBtn}
              onClick={() => setStep(2)}
              disabled={!walletValid || !amountValid}
            >
              {t("ref_continue_btn")}
            </button>
          </>
        ) : step === 2 ? (
          <>
            <div className={styles.withdrawContent}>
              <h2 className={styles.withdrawTitle}>{t("ref_review_title")}</h2>

              <div className={styles.withdrawReviewBody}>
                <div className={styles.withdrawSummary}>
                  <div className={styles.withdrawReviewRow}>
                    <span className={styles.withdrawReviewLabel}>{t("ref_amount_field")}</span>
                    <span className={`${styles.withdrawReviewValue} ${styles.withdrawReviewAmount}`}>
                      {formatUsd(amountValue)}
                    </span>
                  </div>
                  <div className={styles.withdrawReviewRow}>
                    <span className={styles.withdrawReviewLabel}>{t("ref_currency_label")}</span>
                    <span className={styles.withdrawReviewValue}>USDC</span>
                  </div>
                  <div className={styles.withdrawReviewRow}>
                    <span className={styles.withdrawReviewLabel}>{t("ref_erc20_label")}</span>
                    <span className={styles.withdrawReviewValue}>{walletAddress}</span>
                  </div>
                </div>

                <hr className={styles.withdrawDivider} />

                <div className={styles.withdrawReviewRow}>
                  <span className={styles.withdrawReviewLabel}>{t("ref_processing_label")}</span>
                  <span className={styles.withdrawReviewLabel}>{t("ref_processing_time")}</span>
                </div>
              </div>
            </div>

            <div className={styles.withdrawActions}>
              <button
                type="button"
                className={styles.withdrawContinueBtn}
                onClick={handleConfirm}
                disabled={submitting}
              >
                {t("ref_confirm_btn")} <ArrowRight size={24} />
              </button>
              {error && <p className={styles.modalError} role="alert">{error}</p>}
              <button
                type="button"
                className={styles.withdrawBackBtn}
                onClick={() => {
                  setError(null);
                  setStep(1);
                }}
                disabled={submitting}
              >
                {t("ref_back_btn")}
              </button>
            </div>
          </>
        ) : (
          <div className={styles.withdrawSuccess}>
            <div className={styles.withdrawSuccessHeader}>
              <div className={styles.withdrawSuccessIcon}>
                <Check size={40} strokeWidth={1.5} />
              </div>
              <div className={styles.withdrawSuccessHeading}>
                <h2 className={styles.withdrawSuccessTitle}>{t("ref_success_title")}</h2>
                <p className={styles.withdrawSuccessSubtitle}>{t("ref_success_subtitle")}</p>
              </div>
            </div>

            <div className={styles.withdrawSuccessBody}>
              <div className={styles.withdrawSuccessBox}>
                <div className={styles.withdrawReviewRow}>
                  <span className={styles.withdrawReviewLabel}>{t("ref_amount_field")}</span>
                  <span className={`${styles.withdrawReviewValue} ${styles.withdrawReviewAmount}`}>
                    {formatUsd(amountValue)}
                  </span>
                </div>
                <div className={styles.withdrawReviewRow}>
                  <span className={styles.withdrawReviewLabel}>{t("ref_reference_label")}</span>
                  <span className={styles.withdrawReviewLabel}>{reference}</span>
                </div>
              </div>

              <hr className={styles.withdrawDivider} />

              <p className={styles.withdrawSuccessEmail}>
                <span className={styles.withdrawSuccessDot} />
                {t("ref_email_sent")}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default function ReferralsPage() {
  const { t, langCode } = useLanguage();
  const { data, status, reload, applyCode, withdraw } = useReferrals();
  const [tab, setTab] = useState("all");
  const [page, setPage] = useState(1);
  const [shareLinkOpen, setShareLinkOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [enterCodeOpen, setEnterCodeOpen] = useState(false);

  function handleShareLink() {
    setShareLinkOpen(true);
  }

  // Figures come from the API, which applies the hold and the oldest-first payout order
  const referrals = data?.referrals ?? [];
  const summary = data?.summary;
  const minWithdrawal = data?.rules.minWithdrawal ?? 125;
  const canWithdraw = Boolean(summary) && summary.withdrawable >= minWithdrawal;

  const rows = tab === "claimed" ? referrals.filter((r) => r.status === "claimed") : referrals;
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visibleRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const pagination = getVisiblePages(totalPages, safePage);

  const summaryCards = [
    { label: t("ref_traders_referred"), value: summary ? String(summary.tradersReferred) : "–" },
    { label: t("ref_rewards_earned"), value: summary ? formatUsd(summary.rewardsEarned) : "–" },
    {
      label: t("ref_withdrawable"),
      value: summary ? formatUsd(summary.withdrawable) : "–",
      detail: summary && !canWithdraw ? t("ref_min_required") : null,
    },
  ];

  let emptyMessage = null;
  if (status === "loading") emptyMessage = t("ref_loading");
  else if (status === "error") emptyMessage = t("ref_load_error");
  else if (visibleRows.length === 0) emptyMessage = tab === "claimed" ? t("ref_no_claimed") : t("ref_no_referrals");

  return (
    <section className={styles.page}>
      <section className={styles.hero}>
        <div
          className={`${styles.heroTop} d-flex align-items-start justify-content-between`}
        >
          <div>
            <h1 className={styles.heroTitle}>{t("ref_title")}</h1>
            <p className={styles.heroCopy}>{t("ref_copy")}</p>
          </div>

          <div className={styles.heroActions}>
            <Button
              variant="brand-transparent"
              className={`${styles.secondaryButton} ${styles.actionButton}`}
              onClick={() => setEnterCodeOpen(true)}
            >
              {t("ref_enter_code")}
            </Button>
            <Button
              variant="brand-transparent"
              className={`${styles.secondaryButton} ${styles.actionButton}`}
              onClick={handleShareLink}
            >
              {t("ref_share_link")}
            </Button>
            <Button
              variant="default"
              className={`${styles.actionButton} ${styles.primaryAction}`}
              onClick={() => setWithdrawOpen(true)}
              disabled={!canWithdraw}
            >
              {t("ref_withdraw")}
            </Button>
          </div>
        </div>
      </section>

      <section className={styles.summaryGrid}>
        {summaryCards.map((card) => (
          <SummaryCard
            key={card.label}
            label={card.label}
            value={card.value}
            detail={card.detail}
          />
        ))}
      </section>

      <section className={styles.historySection}>
        <div className={styles.historyTop}>
          <h2 className={styles.sectionTitle}>{t("ref_history")}</h2>
          <div className={styles.filterTabs} role="tablist">
            {["all", "claimed"].map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className={`${styles.filterTab} ${tab === key ? styles.filterTabActive : ""}`}
                onClick={() => {
                  setTab(key);
                  setPage(1);
                }}
              >
                {t(`ref_tab_${key}`)}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t("ref_email")}</th>
                <th>{t("ref_date_joined")}</th>
                <th>{t("ref_payout_date")}</th>
                <th>{t("ref_status")}</th>
                <th>{t("ref_your_reward")}</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.id}>
                  <td>{row.email}</td>
                  <td className={styles.mutedCell}>{formatDate(new Date(row.joinedAt), langCode)}</td>
                  <td className={styles.mutedCell}>{row.payoutAt ? formatDate(new Date(row.payoutAt), langCode) : "–"}</td>
                  <td>
                    <span className={`${styles.statusBadge} ${styles[STATUS_CLASS[row.status]]}`}>
                      {t(`ref_status_${row.status}`)}
                    </span>
                  </td>
                  <td className={styles.mutedCell}>{formatUsd(row.reward)}</td>
                </tr>
              ))}
              {emptyMessage && (
                <tr>
                  <td colSpan={5} className={styles.emptyCell}>
                    {emptyMessage}
                    {status === "error" && (
                      <button type="button" className={styles.retryButton} onClick={reload}>
                        {t("ref_retry")}
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <Pagination className={styles.paginationNav}>
            <PaginationContent className={styles.pagination}>
              {pagination.pages.map((pageNumber) => (
                <PaginationItem key={pageNumber}>
                  <PaginationLink
                    className={`${styles.pageButton} ${safePage === pageNumber ? styles.pageButtonActive : ""}`}
                    isActive={safePage === pageNumber}
                    onClick={() => setPage(pageNumber)}
                  >
                    {pageNumber}
                  </PaginationLink>
                </PaginationItem>
              ))}
              {pagination.showEllipsis && (
                <PaginationItem>
                  <PaginationEllipsis
                    className={styles.pageDotsButton}
                    onClick={() => setPage(pagination.ellipsisTarget)}
                    aria-label={`Jump to page ${pagination.ellipsisTarget}`}
                  />
                </PaginationItem>
              )}
            </PaginationContent>
          </Pagination>
        )}
      </section>

      {shareLinkOpen && (
        <ShareLinkModal
          link={data?.referralCode ? referralLink(data.referralCode) : null}
          status={status}
          onClose={() => setShareLinkOpen(false)}
        />
      )}
      {withdrawOpen && (
        <WithdrawModal
          withdrawable={summary.withdrawable}
          minWithdrawal={minWithdrawal}
          onConfirm={withdraw}
          onClose={() => setWithdrawOpen(false)}
        />
      )}
      {enterCodeOpen && (
        <EnterCodeModal
          appliedCode={data?.referredBy}
          onApply={applyCode}
          onClose={() => setEnterCodeOpen(false)}
        />
      )}
    </section>
  );
}
